<template>
<div class="px-1 pt-1">
  <div class="row m-0">
    <div class="col-auto">
      <h2>{{ $t('page.stockIssue.form.title') }}</h2>
    </div>
    <div class="col">
      <div class="form-check m-2">
        <input class="form-check-input" type="checkbox" v-model="requestQuantityInput" id="requestQuantityInput">
        <label class="form-check-label" for="requestQuantityInput">{{ $t('action.requestQuantityInput') }}</label>
      </div>
    </div>
  </div>
  <ScanInput ref="scanInput" :instruction="$t('action.scanBarcode')" @scanned="barcodeScanned" class="input-group" />

  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>

  <form @submit.prevent="postIssue" v-if="itemSignatureConstruct" :disabled="isLoading" class="pe-1">
    <div class="fs-4 d-flex mt-2">
      <ItemField :item="itemSignatureConstruct.item" />
    </div>

    <div v-if="scanResultIncomplete" class="row g-3">

      <div class="col-sm-6 col-md-4 col-lg-3">
        <label for="quantity" class="form-label d-flex align-items-center mb-1">
          <div>{{ $t('field.generic.quantity') }}</div>
          <div class="text-primary ms-1"><UnitQuantity :quantity="currentStockQuantity" :unit-name="itemSignatureConstruct.item.baseUnitCode" :inline-unit-name="true" /></div></label>
        <div class="input-group">
          <input v-model="itemSignatureConstruct.quantity" type="number" min="1" max="10000" class="form-control" id="quantity" ref="quantityInput" :class="{ 'is-invalid': !itemSignatureConstruct.isQuantityValid }">
          <span class="input-group-text">{{ itemSignatureConstruct.item.baseUnitCode }}</span>
        </div>
      </div>

      <div v-if="itemSignatureConstruct.item.trackLotNumber && !itemSignatureConstruct.item.allowCalculatedLot" class="col-sm-6 col-md-4 col-lg-3">
        <label for="lot" class="form-label d-flex align-items-center mb-1">{{ $t('field.masterdata.lot') }} <div class="text-melodi"><UnitQuantity :quantity="itemSignatureConstruct.availableQuantity" :unit-name="itemSignatureConstruct.item.baseUnitCode" :inline-unit-name="true" /></div></label>
        <Typeahead v-model="itemSignatureConstruct.lot" id="lot" ref="lotInput" :inputClass="{ 'form-control': true, 'is-invalid': !itemSignatureConstruct.isLotValid }" :items="currentItemStockLots" :minInputLength="0" :item-suffix="lotSuffix" :allowNew="false" :uppercase="true" />
      </div>

      <div v-if="itemSignatureConstruct.item.trackBestBeforeDate && !itemSignatureConstruct.item.allowCalculatedLot" class="col-sm-6 col-md-4 col-lg-3">
        <label for="bestBefore" class="form-label">{{ $t('field.masterdata.bestBefore') }}</label>
        <input v-model="itemSignatureConstruct.bestBefore" type="date" class="form-control" id="bestBefore" ref="bestBeforeInput" :class="{ 'is-invalid': !itemSignatureConstruct.isBestBeforeValid }">
      </div>

      <div v-if="itemSignatureConstruct.item.trackExpirationDate && !itemSignatureConstruct.item.allowCalculatedLot" class="col-sm-6 col-md-4 col-lg-3">
        <label for="expiry" class="form-label">{{ $t('field.masterdata.expiry') }}</label>
        <input v-model="itemSignatureConstruct.expiry" type="date" class="form-control" id="expiry" ref="expiryInput" :class="{ 'is-invalid': !itemSignatureConstruct.isExpiryValid }">
      </div>

      <div v-if="hasNotEnoughStock" class="d-grid gap-2">
        <div class="alert alert-warning">
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
          {{ $t('error.notEnoughStockShort', { quantity: itemSignatureConstruct.quantity, available: itemSignatureConstruct.availableQuantity, unitName: itemSignatureConstruct.item.baseUnitCode }) }}
        </div>
      </div>
      <div v-else class="d-grid gap-2">
        <button type="submit" class="btn btn-primary btn-lg d-block" :disabled="!itemSignatureConstruct.isValid || isLoading"><font-awesome-icon :icon="['fas', 'minus']" /> {{ $t('page.stockIssue.form.confirm') }}</button>
      </div>

    </div>
  </form>
</div>
</template>

<script setup lang="ts">
import ScanInput from "@/components/form/ScanInput.vue";
import { onMounted, ref, nextTick, watch, computed } from "vue";
import { storeToRefs } from 'pinia';
import api from "@/services/api";
import IGS1 from "@/models/IGS1";
import { useSessionStore, useUserSettingsStore, useItemsStore, useTradeItemsStore, useStockStore } from "@/stores";
import { toast } from "vue3-toastify";
import DecreaseStockRequest from "@/models/requests/IDecreaseStockRequest";
import { useI18n } from "vue-i18n";
import { ItemField } from '@/components/fields';
import UnitQuantity from "@/components/UnitQuantity.vue";
import { AxiosError } from "axios";
import FrontendStock from "@/models/frontend/Stock";
import { compareOptionalDate, compareOptionalString } from "@/services";
import SignatureItem from "@/models/frontend/SignatureItem";

const userSettingsStore = useUserSettingsStore();
const { data: userSettings } = storeToRefs(userSettingsStore);
const i18n = useI18n();

const isLoading = ref(false);
const error = ref('');
const scanResultIncomplete = ref(false);
const itemSignatureConstruct = ref<SignatureItem | undefined>();
const requestQuantityInput = ref(false);

const currentStockQuantity = ref<number>(0);
const currentItemStock = ref<FrontendStock[]>();
const currentItemStockLots = ref<string[]>([]);

const scanInput = ref<InstanceType<typeof ScanInput> | null>(null);
const bestBeforeInput = ref<HTMLInputElement | null>(null);
const expiryInput = ref<HTMLInputElement | null>(null);
const lotInput = ref<HTMLInputElement | null>(null);
const quantityInput = ref<HTMLInputElement | null>(null);

const itemStore = useItemsStore();
const tradeItemStore = useTradeItemsStore();
const stockStore = useStockStore();
const { error: stockError } = storeToRefs(stockStore);

const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);

onMounted(async () => {
  requestQuantityInput.value = userSettings.value.confirmQuantity ?? false;
});

const getDateString = (date: Date | undefined) => {
  if (!date) { return undefined; }
  const offset = date.getTimezoneOffset();
  date = new Date(date.getTime() - (offset * 60 * 1000));
  return date.toISOString().split('T')[0];
}

watch(itemSignatureConstruct, () => {
  autoFill();
}, { deep: true });

const barcodeScanned = async (barcode: string, gs1: IGS1 | undefined) => {
  scanResultIncomplete.value = true; // used to show/hide the form fields after successful scanning, but incomplete signature
  itemSignatureConstruct.value = undefined; // reset item signature construct
  // search item
  if (barcode || gs1) {
    const contentCode = gs1?.gtin ?? barcode;
    var tradeItem = tradeItemStore.getByCode(contentCode);
    if (tradeItem) {
      const item = itemStore.getById(tradeItem.itemId);
      if (item) {
        itemSignatureConstruct.value = new SignatureItem(item, item.baseUnitCode, 1);
      }
    }
    if (!itemSignatureConstruct.value && gs1?.itemCode) {
      const item = itemStore.getByCode(gs1.itemCode);
      if (item) {
        itemSignatureConstruct.value = new SignatureItem(item, item.baseUnitCode, 1);
      }
    }
  }

  // if not matched already, try get item by code
  if (!itemSignatureConstruct.value) {
    const item = itemStore.getByCode(barcode);
    if (item) {
      itemSignatureConstruct.value = new SignatureItem(item, item.baseUnitCode, 1);
    }
  }

  if (!itemSignatureConstruct.value) {
    error.value = i18n.t('error.unknownItem') + ': "' + barcode + '"';
    return;
  }

  error.value = '';

  if (gs1?.quantity) {
    itemSignatureConstruct.value.quantity = gs1.quantity * itemSignatureConstruct.value.quantity;
  }

  if (requestQuantityInput.value) {
    // this is required to invalidate the form (disabled submit button)
    itemSignatureConstruct.value.quantity = 0;
  }

  if (itemSignatureConstruct.value?.item.trackLotNumber && !itemSignatureConstruct.value.item?.allowCalculatedLot) {
    if (gs1?.lot) {
      itemSignatureConstruct.value.lot = gs1.lot;
    }
  }

  if (itemSignatureConstruct.value?.item.trackBestBeforeDate && !itemSignatureConstruct.value.item?.allowCalculatedLot) {
    if (gs1?.bestBefore) {
      itemSignatureConstruct.value.bestBefore = getDateString(gs1.bestBefore);
    }
  }

  if (itemSignatureConstruct.value?.item.trackExpirationDate && !itemSignatureConstruct.value.item?.allowCalculatedLot) {
    if (gs1?.expiry) {
      itemSignatureConstruct.value.expiry = getDateString(gs1.expiry);
    }
  }

  await fetchCurrentItemStock();
  currentStockQuantity.value = currentItemStock.value?.reduce((acc, stock) => acc + stock.quantity, 0) ?? 0;
  currentItemStockLots.value = [...new Set(currentItemStock.value?.map((stock) => stock.lot ?? '') ?? [])]; // set is used to remove duplicates
  itemSignatureConstruct.value.availableQuantity = currentStockQuantity.value;

  autoFill();

  // if we did not have any error fields, directly post the receipt now
  if (itemSignatureConstruct.value.isValid) {
    if (requestQuantityInput.value) {
      scanResultIncomplete.value = true;
      await fetchCurrentStockQuantity();
      await nextTick(() => {
        const qIn = quantityInput.value;
        if (qIn) qIn.value = "";
        quantityInput.value?.select();
      });
      return;
    }
    await postIssue();
  }
  else {
    scanResultIncomplete.value = true;
    const itemWithSignature = itemSignatureConstruct.value;
    await nextTick(() => {
      if (!itemWithSignature.isQuantityValid) quantityInput.value?.select();
      else if (!itemWithSignature.isLotValid) document.getElementById("lot")?.getElementsByTagName('input')[0]!.focus();
      else if (!itemWithSignature.isBestBeforeValid) bestBeforeInput.value?.focus();
      else if (!itemWithSignature.isExpiryValid) expiryInput.value?.focus();
    });
  }
};

const autoFill = () => {
  const isc = itemSignatureConstruct.value;
  if (!isc) { return; }

  // auto fill lot, if only one lot is available
  if (isc.item.trackLotNumber && !isc.item.allowCalculatedLot && !isc.lot) {
    const uniqueLots = [...new Set(currentItemStock.value?.map(stock => stock.lot).filter(lot => lot) ?? [])];
    if (uniqueLots.length === 1) {
      isc.lot = uniqueLots[0];
    }
  }

  // auto fill best before date, if only one best before date is available
  if ((isc.item.trackBestBeforeDate && !isc.item.allowCalculatedLot) && isc.lot) {
    const stocksWithSameLot = currentItemStock.value?.filter(stock => stock.lot === isc.lot) ?? [];
    const uniqueBestBeforDates = [...new Set(stocksWithSameLot.map(stock => stock.bestBefore).filter(bestBefore => bestBefore))];

    console.debug('uniqueBestBeforDates', uniqueBestBeforDates);
    if (uniqueBestBeforDates.length === 1) {
      isc.bestBefore = uniqueBestBeforDates[0];
    }
  }

  // auto fill expiry date, if only one expiry date is available
  if ((isc.item.trackExpirationDate && !isc.item.allowCalculatedLot) && isc.lot) {
    const stocksWithSameLot = currentItemStock.value?.filter(stock => stock.lot === isc.lot) ?? [];
    const uniqueExpiries = [...new Set(stocksWithSameLot.map(stock => stock.expiry).filter(expiry => expiry))];

    console.debug('uniqueExpiries', uniqueExpiries);
    if (uniqueExpiries.length === 1) {
      isc.expiry = uniqueExpiries[0];
    }
  }

  const stocksWithSameSignature = currentItemStock.value?.filter(stock =>
    (!isc.item.trackLotNumber || isc.item.allowCalculatedLot || stock.lot === isc.lot) &&
    (!isc.item.trackBestBeforeDate || isc.item.allowCalculatedLot || stock.bestBefore === isc.bestBefore) &&
    (!isc.item.trackExpirationDate || isc.item.allowCalculatedLot || stock.expiry === isc.expiry))
    ?? [];
  isc.availableQuantity = stocksWithSameSignature.reduce((acc, stock) => acc + stock.quantity, 0);
  console.debug('signature stock quantity', isc.availableQuantity);
};

// Function to suffix lot display text for typeahead
const lotSuffix = (lot: string): string => {
  // append quantity for lot
  const stocksWithSameLot = currentItemStock.value?.filter(stock => stock.lot === lot) ?? [];
  if (stocksWithSameLot.length === 0) {
    return ''; // no stocks with this lot, just return the lot
  }
  return '<span class="text-secondary"> (' + stocksWithSameLot.reduce((acc, stock) => acc + stock.quantity, 0) + ' ' + itemSignatureConstruct.value?.item.baseUnitCode + ')</span>';
}

const hasNotEnoughStock = computed(() => {
  const isc = itemSignatureConstruct.value;
  if (!isc) { return false; }
  if (!isc.isValid) { return false; }
  return isc.quantity > isc.availableQuantity;
});

const fetchCurrentStockQuantity = async () => {
  if (!itemSignatureConstruct.value?.item) { return; }
  await api
    .get<number>(`v1.0/stocks/.qty?siteId=${selectedSiteId.value}&itemId=${itemSignatureConstruct.value.item.id}`)
    .then((response) => {
      // currentStockQuantity.value = response.data.reduce((acc, stock) => acc + stock.quantity, 0);
      currentStockQuantity.value = response.data;
    })
    .catch((error) => (error = { error }));
}

const fetchCurrentItemStock = async () => {
  if (!itemSignatureConstruct.value?.item) { return; }
  const stocksStore = useStockStore();
  const itemStock = await stocksStore.fetchAllByItem(itemSignatureConstruct.value.item.id);
  currentItemStock.value = itemStock.sort((a, b) =>
    a.item.name.localeCompare(b.item.name) ||
    compareOptionalDate(a.expiryAsDate, b.expiryAsDate) ||
    compareOptionalDate(a.bestBeforeAsDate, b.bestBeforeAsDate) ||
    compareOptionalString(a.lot, b.lot)
  );
}

const postIssue = async () => {
  if (!itemSignatureConstruct.value) { return; }

  requestQuantityInput.value = userSettings.value.confirmQuantity ?? false;
  isLoading.value = true;

  const request: DecreaseStockRequest = {
    siteId: selectedSiteId.value,
    itemId: itemSignatureConstruct.value.item.id,
    quantity: itemSignatureConstruct.value.quantity!,
    lot: itemSignatureConstruct.value.lot,
    bestBefore: itemSignatureConstruct.value.bestBefore,
    expiry: itemSignatureConstruct.value.expiry
  };

  await stockStore.postStockDecreaseSpill(request);
  await fetchCurrentStockQuantity();

  if (stockError.value instanceof AxiosError)
  {
    const axiosError = stockError.value as AxiosError;
    toast.error(i18n.t('error.axiosErrorMessage', { code: axiosError.code, message: axiosError.message }));
  }
  else if (stockError.value)
  {
    toast.error(i18n.t('page.stockIssue.errorMessage', { error: stockError.value }));
  }
  else {
    toast.info(i18n.t('page.stockIssue.successMessage', { quantity: itemSignatureConstruct.value.quantity, item: itemSignatureConstruct.value.item.description, unit: itemSignatureConstruct.value.item.baseUnitCode }));
  }

  itemSignatureConstruct.value = undefined;
  isLoading.value = false;
  scanInput.value?.focus();
}
</script>
